import AsyncStorage from '@react-native-async-storage/async-storage';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';
import type { MemberType } from '@/components/People/PeopleFilterSelector';
import normalizeInviteCode from '@/utils/normalizeInviteCode';

export type JoinKind = 'organization' | 'team';

export type JoinIntent = {
  code: string;
  kind: JoinKind;
  memberType?: MemberType;
};

type JoinIntentState = {
  beginJoinCompletion: () => void;
  captureJoinIntent: (intent: JoinIntent) => void;
  clearJoinIntent: () => void;
  discardJoinIntent: (intent: JoinIntent) => void;
  discardedJoinIntentKey: string | null;
  finishJoinCompletion: () => void;
  hasHydratedJoinIntent: boolean;
  joinCompletionPending: boolean;
  joinIntent: JoinIntent | null;
  joinResumePending: boolean;
  markJoinIntentForResume: () => void;
  restoreJoinIntentAfterCompletionFailure: (intent: JoinIntent) => void;
  setJoinIntent: (intent: JoinIntent) => void;
};

function normalizeJoinIntent(intent: JoinIntent): JoinIntent {
  return {
    code: normalizeInviteCode(intent.code),
    kind: intent.kind,
    memberType: intent.memberType,
  };
}

function getJoinIntentKey(intent: JoinIntent) {
  return `${intent.kind}:${intent.code}:${intent.memberType ?? 'default'}`;
}

function createJoinIntentStore() {
  let markHydrated = () => {};

  return createWithEqualityFn<JoinIntentState>()(
    persist(
      (set) => {
        markHydrated = () => set({ hasHydratedJoinIntent: true });

        return {
          beginJoinCompletion: () =>
            set({
              discardedJoinIntentKey: null,
              joinCompletionPending: true,
              joinIntent: null,
              joinResumePending: false,
            }),
          captureJoinIntent: (intent) =>
            set((state) => {
              const normalizedIntent = normalizeJoinIntent(intent);

              if (
                state.discardedJoinIntentKey ===
                getJoinIntentKey(normalizedIntent)
              ) {
                return {};
              }

              return {
                discardedJoinIntentKey: null,
                joinIntent: normalizedIntent,
                joinResumePending: false,
              };
            }),
          clearJoinIntent: () =>
            set({
              discardedJoinIntentKey: null,
              joinCompletionPending: false,
              joinIntent: null,
              joinResumePending: false,
            }),
          discardJoinIntent: (intent) => {
            const normalizedIntent = normalizeJoinIntent(intent);

            set({
              discardedJoinIntentKey: getJoinIntentKey(normalizedIntent),
              joinCompletionPending: false,
              joinIntent: null,
              joinResumePending: false,
            });
          },
          discardedJoinIntentKey: null,
          finishJoinCompletion: () => set({ joinCompletionPending: false }),
          hasHydratedJoinIntent: false,
          joinCompletionPending: false,
          joinIntent: null,
          joinResumePending: false,
          markJoinIntentForResume: () =>
            set((state) => {
              if (!state.joinIntent) return {};

              return { joinResumePending: true };
            }),
          restoreJoinIntentAfterCompletionFailure: (intent) =>
            set({
              discardedJoinIntentKey: null,
              joinCompletionPending: false,
              joinIntent: normalizeJoinIntent(intent),
              joinResumePending: true,
            }),
          setJoinIntent: (intent) =>
            set({
              discardedJoinIntentKey: null,
              joinCompletionPending: false,
              joinIntent: normalizeJoinIntent(intent),
              joinResumePending: false,
            }),
        };
      },
      {
        name: 'useJoinIntentStore',
        onRehydrateStorage: () => () => {
          markHydrated();
        },
        partialize: (state) => ({
          joinIntent: state.joinIntent,
          joinResumePending: state.joinResumePending,
        }),
        storage: createJSONStorage(() => AsyncStorage),
      },
    ),
  );
}

const useJoinIntentStore = createJoinIntentStore();

export default useJoinIntentStore;
